import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Key, Plus, Trash2, Copy, Power } from 'lucide-react';

export function ApiKeysCard({ 
  apiTokens, 
  setApiTokens,
  newApiToken,
  setNewApiToken,
  createdApiKey,
  setCreatedApiKey,
  onCreateToken,
  onDeleteToken,
  onToggleToken,
  saving 
}) {
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiato negli appunti!');
  };

  const togglePermission = (perm) => {
    const perms = newApiToken.permissions.includes(perm)
      ? newApiToken.permissions.filter(p => p !== perm)
      : [...newApiToken.permissions, perm];
    setNewApiToken({ ...newApiToken, permissions: perms });
  };

  return (
    <Card className="border-border bg-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Key className="w-5 h-5 text-purple-500" />
              API Keys Esterne
            </CardTitle>
            <CardDescription>
              Crea API Keys per integrazioni server-to-server (senza login/OTP)
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Created API Key Alert */}
        {createdApiKey && (
          <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
            <p className="text-sm font-medium text-green-500 mb-2">API Key creata con successo!</p>
            <p className="text-xs text-muted-foreground mb-2">Copia questa chiave ora. Non sara' piu' visibile.</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 p-2 bg-background rounded text-xs font-mono break-all">
                {createdApiKey}
              </code>
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard(createdApiKey)}
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <Button
              size="sm"
              variant="ghost"
              className="mt-2 text-muted-foreground"
              onClick={() => setCreatedApiKey(null)}
            >
              Ho copiato, chiudi
            </Button>
          </div>
        )}

        {/* Create New API Key */}
        <div className="p-4 border border-border rounded-lg space-y-4">
          <h4 className="font-medium">Crea Nuova API Key</h4>
          
          <div className="space-y-2">
            <Label>Nome *</Label>
            <Input
              data-testid="api-token-name"
              placeholder="Es: Integrazione E-commerce"
              value={newApiToken.name}
              onChange={(e) => setNewApiToken({ ...newApiToken, name: e.target.value })}
            />
          </div>

          <div className="space-y-2">
            <Label>Permessi *</Label>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant={newApiToken.permissions.includes('redeem_token') ? 'default' : 'outline'}
                onClick={() => togglePermission('redeem_token')}
                data-testid="perm-redeem-token"
              >
                redeem_token
              </Button>
              <Button
                type="button"
                size="sm"
                variant={newApiToken.permissions.includes('create_token') ? 'default' : 'outline'}
                onClick={() => togglePermission('create_token')}
                data-testid="perm-create-token"
              >
                create_token
              </Button>
              <Button
                type="button"
                size="sm"
                variant={newApiToken.permissions.includes('send_notification') ? 'default' : 'outline'}
                onClick={() => togglePermission('send_notification')}
                data-testid="perm-send-notification"
              >
                send_notification
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              <strong>redeem_token:</strong> Riscatta token per utenti | <strong>create_token:</strong> Crea/elimina token OBC | <strong>send_notification:</strong> Invia notifiche push agli utenti OBC
            </p>
          </div>

          <Button
            onClick={onCreateToken}
            disabled={saving}
            data-testid="create-api-token"
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4 mr-2" />
            {saving ? 'Creazione...' : 'Crea API Key'}
          </Button>
        </div>

        <Separator />

        {/* Existing API Keys */}
        <div className="space-y-3">
          <h4 className="font-medium">API Keys Esistenti</h4>
          
          {apiTokens.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              Nessuna API Key creata
            </p>
          ) : (
            <div className="space-y-2">
              {apiTokens.map((token) => (
                <div
                  key={token.token_id}
                  className={`p-3 border rounded-lg flex items-center justify-between ${
                    token.is_active ? 'border-border bg-card' : 'border-red-500/30 bg-red-500/5'
                  }`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{token.name}</span>
                      {!token.is_active && (
                        <span className="text-xs bg-red-500/20 text-red-500 px-2 py-0.5 rounded">
                          Disattivata
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      <code>{token.api_key}</code>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Permessi: {token.permissions?.join(', ') || 'nessuno'}
                      {token.last_used && ` | Ultimo uso: ${new Date(token.last_used).toLocaleDateString('it-IT')}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onToggleToken(token.token_id)}
                      title={token.is_active ? 'Disattiva' : 'Attiva'}
                    >
                      <Power className={`w-4 h-4 ${token.is_active ? 'text-green-500' : 'text-red-500'}`} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-500 hover:text-red-600"
                      onClick={() => onDeleteToken(token.token_id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-3 bg-muted/30 rounded-lg border border-border">
          <p className="text-sm text-muted-foreground">
            <strong>Uso:</strong> Includi l'API Key nell'header <code>X-API-Key</code> delle richieste HTTP.
            <br />
            <strong>Documentazione:</strong> Consulta <code>/API_DOCS.md</code> per esempi PHP e cURL.
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
